import React, { useState } from 'react';
import { useDualWriteStats } from './useDualWriteStats';
import { ErrorDetailView } from './ErrorDetailView';
import { StatCard } from './StatCard';
import { RulesPanel } from '../../ui/RulesPanel';
import { applyWarningRules, countWarningSuppression } from './warningRules';
import type { WarningRule } from './warningRules';
import { MissingFieldsDetailsSchema } from './types';
import type { DualWriteEvent, MissingFieldEntry } from './types';

// Helpers

/** Extract missing field entries from a warning event */
function getMissing(evt: DualWriteEvent): MissingFieldEntry[] {
  const parsed = MissingFieldsDetailsSchema.safeParse(evt.errorDetails);
  return parsed.success ? parsed.data.missing : [];
}

// Props

interface WarningsTabProps {
  events: DualWriteEvent[];  // ALL warning events (unfiltered)
  rules: WarningRule[];
  onToggleRule: (ruleId: string) => void;
}

// Component

export function WarningsTab({ events, rules, onToggleRule }: WarningsTabProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [rulesExpanded, setRulesExpanded] = useState<boolean>(false);
  const [lookupsExpanded, setLookupsExpanded] = useState<boolean>(false);

  // Fetch legacy field lookups from the stats endpoint (cached by react-query)
  const { data: statsData } = useDualWriteStats({ staleTime: 0 });
  const legacyLookups = statsData?.legacyFieldLookups ?? [];

  // Apply rules to get active warnings
  const warnings = applyWarningRules(events, rules);
  const suppressedCount = events.length - warnings.length;
  const suppressionCounts = countWarningSuppression(events, rules);
  const activeRuleCount = rules.filter((r) => r.enabled).length;

  // Group warnings by blueprint kind
  const byKind = new Map<string, DualWriteEvent[]>();
  for (const w of warnings) {
    const kind = w.blueprintKind ?? 'unknown';
    if (!byKind.has(kind)) byKind.set(kind, []);
    byKind.get(kind)!.push(w);
  }

  // Group warnings by missing field with legacy status counts
  const byField = new Map<string, { count: number; sources: Set<string>; sourceGaps: number; mappingBugs: number }>();
  for (const w of warnings) {
    for (const m of getMissing(w)) {
      const existing = byField.get(m.field);
      if (existing) {
        existing.count++;
        if (m.source) existing.sources.add(m.source);
        if (m.legacyPresent === true) existing.mappingBugs++;
        else if (m.legacyPresent === false) existing.sourceGaps++;
      } else {
        const sources = new Set<string>();
        if (m.source) sources.add(m.source);
        byField.set(m.field, {
          count: 1,
          sources,
          sourceGaps: m.legacyPresent === false ? 1 : 0,
          mappingBugs: m.legacyPresent === true ? 1 : 0,
        });
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Rules button + collapsible panel */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-sm font-medium text-content-tertiary">Warning Summary</h3>

          <button
            type="button"
            className={`text-xs px-2 py-1 rounded-md cursor-pointer transition-colors inline-flex items-center gap-1.5 ${
              rulesExpanded
                ? 'bg-blueberry-100 text-blueberry-800 ring-1 ring-blueberry-300'
                : 'text-icon-subtle hover:bg-bg-secondary'
            }`}
            onClick={() => setRulesExpanded((prev) => !prev)}
          >
            Rules
            {activeRuleCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-blueberry-500 text-white text-[10px] font-semibold">
                {activeRuleCount}
              </span>
            )}
          </button>

          {suppressedCount > 0 && (
            <span className="text-xs text-icon-subtle">
              {suppressedCount} warning{suppressedCount !== 1 ? 's' : ''} suppressed
            </span>
          )}
        </div>

        {/* Collapsible rules panel */}
        {rulesExpanded && (
          <div className="rounded-lg bg-bg-secondary border border-secondary p-3 space-y-3 max-h-[60vh] overflow-y-auto mb-4">
            <div className="text-[10px] font-semibold text-content-tertiary uppercase tracking-wide">Warning Rules</div>
            <RulesPanel
              rules={rules}
              onToggle={onToggleRule}
              suppressionCounts={new Map(
                [...suppressionCounts.entries()].map(([id, d]) => [id, { count: d.count, breakdown: d.kinds }]),
              )}
              breakdownLabel="kinds"
            />

            {/* Legacy field lookups — how legacyPresent is determined */}
            {legacyLookups.length > 0 && (
              <div className="border-t border-secondary pt-3">
                <button
                  type="button"
                  className="text-[10px] font-semibold text-content-tertiary uppercase tracking-wide cursor-pointer hover:text-content-secondary transition-colors flex items-center gap-1"
                  onClick={() => setLookupsExpanded((prev) => !prev)}
                >
                  Legacy Field Lookups
                  <span className="text-content-tertiary">{lookupsExpanded ? '▾' : '▸'}</span>
                </button>
                {lookupsExpanded && (
                  <div className="mt-2 space-y-1">
                    <p className="text-[10px] text-content-tertiary mb-2">
                      Maps V4 blueprint field names to their legacy row locations. Used to determine legacyPresent — whether the data exists in legacy under a different column name. Fields not listed here fall back to row.content (dynamic Salesforce fields).
                    </p>
                    <div className="rounded border border-secondary overflow-hidden">
                      <table className="w-full text-[11px]">
                        <thead className="bg-bg-tertiary">
                          <tr>
                            <th className="px-2 py-1 text-left font-semibold text-content-tertiary">Blueprint Field</th>
                            <th className="px-2 py-1 text-left font-semibold text-content-tertiary">Legacy Source</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary">
                          {legacyLookups.map(({ field, source }) => (
                            <tr key={field}>
                              <td className="px-2 py-1 font-mono text-content-primary">{field}</td>
                              <td className="px-2 py-1 font-mono text-content-secondary">{source}</td>
                            </tr>
                          ))}
                          <tr>
                            <td className="px-2 py-1 font-mono text-content-tertiary italic">*</td>
                            <td className="px-2 py-1 font-mono text-content-tertiary italic">row.content[field]</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard label="Total Warnings" value={warnings.length} subtitle={suppressedCount > 0 ? `+${suppressedCount} suppressed` : undefined} accent={warnings.length > 0 ? 'warning' : undefined} />
          <StatCard label="Affected Blueprints" value={byKind.size} />
          <StatCard label="Missing Fields" value={byField.size} />
          <StatCard label="Unique Artifacts" value={new Set(warnings.map(w => w.externalId)).size} />
        </div>
      </div>

      {/* By Blueprint Kind */}
      {byKind.size > 0 && (
        <div>
          <h3 className="text-sm font-medium text-content-tertiary mb-3">By Blueprint</h3>
          <div className="rounded-lg border border-border-secondary overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-bg-secondary text-left text-xs text-icon-subtle uppercase">
                <tr>
                  <th className="px-3 py-2">Blueprint Kind</th>
                  <th className="px-3 py-2">Warnings</th>
                  <th className="px-3 py-2">Missing Fields</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-tertiary">
                {[...byKind.entries()].sort((a, b) => b[1].length - a[1].length).map(([kind, evts]) => {
                  const fields = new Set<string>();
                  for (const e of evts) {
                    for (const m of getMissing(e)) fields.add(m.field);
                  }
                  return (
                    <tr key={kind} className="hover:bg-bg-secondary">
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center rounded-full bg-blueberry-50 px-2 py-0.5 text-xs font-medium text-blueberry-700">
                          {kind}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{evts.length}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {[...fields].map(f => (
                            <span
                              key={f}
                              className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-mono bg-tangerine-50 text-tangerine-600"
                            >
                              {f}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* By Missing Field */}
      {byField.size > 0 && (
        <div>
          <h3 className="text-sm font-medium text-content-tertiary mb-3">Missing Fields</h3>
          <div className="rounded-lg border border-border-secondary overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-bg-secondary text-left text-xs text-icon-subtle uppercase">
                <tr>
                  <th className="px-3 py-2">Field</th>
                  <th className="px-3 py-2">Occurrences</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-tertiary">
                {[...byField.entries()].sort((a, b) => b[1].count - a[1].count).map(([field, info]) => (
                  <tr key={field} className="hover:bg-bg-secondary">
                    <td className="px-3 py-2 font-mono text-xs">{field}</td>
                    <td className="px-3 py-2 font-mono text-xs">{info.count}</td>
                    <td className="px-3 py-2 text-xs text-icon-subtle">{[...info.sources].join(', ')}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {info.mappingBugs > 0 && (
                          <span className="inline-flex items-center rounded-full bg-bg-red-light px-1.5 py-0.5 text-[10px] font-medium text-content-danger">
                            {info.mappingBugs} mapping bug
                          </span>
                        )}
                        {info.sourceGaps > 0 && (
                          <span className="inline-flex items-center rounded-full bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-icon-subtle">
                            {info.sourceGaps} source gap
                          </span>
                        )}
                        {info.mappingBugs === 0 && info.sourceGaps === 0 && (
                          <span className="text-icon-subtle">&mdash;</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Individual warning events */}
      <div>
        <h3 className="text-sm font-medium text-content-tertiary mb-3">
          All Warnings
          <span className="ml-2 text-xs font-normal text-icon-subtle">({warnings.length})</span>
        </h3>
        {warnings.length === 0 ? (
          <div className="text-sm text-icon-subtle py-4">
            {suppressedCount > 0
              ? `All ${suppressedCount} warnings are suppressed by rules.`
              : 'No warnings recorded this session.'}
          </div>
        ) : (
          <div className="rounded-lg border border-border-secondary overflow-hidden max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-bg-secondary text-left text-icon-subtle uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-1.5">Time</th>
                  <th className="px-3 py-1.5">Kind</th>
                  <th className="px-3 py-1.5">Legacy ID</th>
                  <th className="px-3 py-1.5">Missing Fields</th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-tertiary">
                {warnings.map((evt, wi) => {
                  const eventKey = `${evt.timestamp}-${evt.legacyId}-${evt.type}-${wi}`;
                  const isExpanded = expandedKey === eventKey;
                  const missing = getMissing(evt);
                  return (
                    <React.Fragment key={eventKey}>
                      <tr
                        className="bg-tangerine-50/50 cursor-pointer hover:bg-tangerine-50"
                        onClick={() => setExpandedKey(isExpanded ? null : eventKey)}
                      >
                        <td className="px-3 py-1.5 font-mono text-icon-subtle">
                          {new Date(evt.timestamp).toLocaleTimeString()}
                        </td>
                        <td className="px-3 py-1.5">
                          <span className="inline-flex items-center rounded-full bg-blueberry-50 px-1.5 py-0.5 text-xs font-medium text-blueberry-700">
                            {evt.blueprintKind ?? '\u2014'}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 font-mono text-content-tertiary">{evt.legacyId}</td>
                        <td className="px-3 py-1.5">
                          {missing.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {missing.map((m: MissingFieldEntry) => (
                                <span
                                  key={m.field}
                                  className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-mono bg-tangerine-50 text-tangerine-600"
                                >
                                  {m.field}
                                </span>
                              ))}
                            </div>
                          ) : '\u2014'}
                        </td>
                        <td className="px-3 py-1.5 text-icon-subtle">
                          {isExpanded ? '\u25BE' : '\u25B8'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-tangerine-50/30">
                          <td colSpan={5} className="px-3 py-3">
                            <div className="space-y-2">
                              <div className="text-xs">
                                <span className="font-medium text-tangerine-600">Warning: </span>
                                <span className="font-mono text-tangerine-500">{evt.errorCode}</span>
                                <span className="text-content-tertiary"> — {evt.error}</span>
                              </div>
                              {evt.errorDetails && (
                                <div>
                                  <div className="text-xs font-medium text-content-tertiary mb-1">Details</div>
                                  <ErrorDetailView errorCode={evt.errorCode} details={evt.errorDetails} />
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
